import { useState, type FocusEvent } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { ChevronDown } from "lucide-react";
import { ES_NAV_GROUPS, ES_NAV_LINKS, ES_PAGES, type EsNavKey } from "@/lib/es-pages";

const linkClass =
  "text-brand-deep hover:underline underline-offset-4";

function NavLink({ page, className }: { page: EsNavKey; className?: string }) {
  const item = ES_PAGES[page];
  return (
    <Link
      to={item.es}
      hrefLang="es"
      lang="es"
      className={className ?? linkClass}
      activeProps={{ className: "font-semibold underline underline-offset-4" }}
      activeOptions={{ exact: true }}
    >
      {item.label}
    </Link>
  );
}

function DesktopNavGroup({
  group,
}: {
  group: (typeof ES_NAV_GROUPS)[number];
}) {
  const [open, setOpen] = useState(false);

  const handleBlur = (event: FocusEvent<HTMLLIElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setOpen(false);
    }
  };

  return (
    <li
      className="relative hidden md:block"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onBlur={handleBlur}
    >
      <button
        type="button"
        aria-expanded={open}
        className="flex cursor-pointer items-center gap-1 text-brand-deep underline-offset-4 hover:underline"
        onClick={() => setOpen((current) => !current)}
        onFocus={() => setOpen(true)}
      >
        {group.label}
        <ChevronDown
          size={14}
          aria-hidden="true"
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open ? (
        <section className="absolute left-1/2 top-full z-20 w-60 -translate-x-1/2 pt-2">
          <ul className="flex flex-col gap-1 rounded-xl border border-border bg-card p-3 text-left shadow-sm">
            {group.items.map((key) => (
              <li key={key}>
                <NavLink page={key} className="block py-0.5 text-brand-deep hover:underline" />
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </li>
  );
}

/**
 * Spanish-only section navigation. It is rendered exclusively from the /es
 * page shell, so English routes keep their own header untouched and deep
 * Spanish pages can stay `inNav: false` while still being reachable here.
 */
export function EsNav({ className = "" }: { className?: string }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  return (
    <nav aria-label="Secciones en español" lang="es" className={className}>
      <ul className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm">
        <li>
          <NavLink page="home" />
        </li>

        {ES_NAV_GROUPS.map((group) => {
          const open = group.items.some((key) => pathname.startsWith(ES_PAGES[key].es));
          return (
            <li key={`${group.label}-mobile`} className="relative md:hidden">
              <details open={open} className="group">
                <summary className="flex cursor-pointer list-none items-center gap-1 text-brand-deep marker:hidden hover:underline underline-offset-4">
                  {group.label}
                  <ChevronDown
                    size={14}
                    aria-hidden="true"
                    className="transition-transform group-open:rotate-180"
                  />
                </summary>
                <ul className="mt-2 flex flex-col gap-1 rounded-xl border border-border bg-card p-3 text-left shadow-sm md:absolute md:left-1/2 md:z-20 md:w-60 md:-translate-x-1/2">
                  {group.items.map((key) => (
                    <li key={key}>
                      <NavLink page={key} className="block py-0.5 text-brand-deep hover:underline" />
                    </li>
                  ))}
                </ul>
              </details>
            </li>
          );
        })}

        {ES_NAV_GROUPS.map((group) => (
          <DesktopNavGroup key={`${group.label}-desktop`} group={group} />
        ))}

        {ES_NAV_LINKS.filter((key) => key !== "home").map((key) => (
          <li key={key}>
            <NavLink
              page={key}
              className={
                key === "contact"
                  ? "rounded-full bg-brand-soft/60 px-3 py-1 font-semibold text-brand-deep hover:bg-brand-soft"
                  : linkClass
              }
            />
          </li>
        ))}
      </ul>
    </nav>
  );
}
